import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatInput } from '@angular/material/input';
import { FormInlineErrorComponent, CountySelectorComponent } from '@k12/forms';
import { SubTask, Task } from '@k12/models';
import { TaskTypeAction } from '@k12/models';
import { MatCard, MatCardActions } from '@angular/material/card';
import { take, tap } from 'rxjs';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';
import { MatSelect, MatOption } from '@angular/material/select';
import { Router } from '@angular/router';
import { StudentsService } from '@k12/school-services';
import { nameValidator } from 'projects/libs/validators';

type CoEnrollmentType = {
  id: number;
  name: string;
  description: string;
};

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-home-school-registration-information',
  imports: [
    MatInput,
    ReactiveFormsModule,
    MatCard,
    MatSlideToggle,
    MatCardActions,
    MatButton,
    MatSelect,
    MatOption,
    MatFormField,
    FormInlineErrorComponent,
    CountySelectorComponent
  ],
  templateUrl: './home-school-registration-information.html',
  styleUrl: './home-school-registration-information.scss'
})
export class HomeSchoolRegistrationInformation implements OnInit {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly studentsService = inject(StudentsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  @Input() task: Task | null;
  @Input() taskTypeAction: TaskTypeAction | null;
  @Input() taskAction: SubTask | null;
  @Output() submitFormData: EventEmitter<string> = new EventEmitter<string>();

  protected viewMode: 'readonly' | 'edit' = 'edit';
  protected form: FormGroup = this.formBuilder.group({
    studentName: ['loading...'],
    studentPid: ['loading...'],
    homeschoolId: ['', Validators.required],
    homeschoolName: ['', [Validators.required, nameValidator()]],
    administratorName: ['', [Validators.required, nameValidator()]],
    county: ['', Validators.required],
    isFullTime: [true, Validators.required],
    coEnrollmentType: [null]
  });

  protected coEnrollmentTypes: CoEnrollmentType[] = [
    { id: 1, name: 'Public', description: 'Child is also attending public school part-time.' },
    { id: 2, name: 'Private', description: 'Child is also attending another private school part-time.' },
    { id: 3, name: 'Home School', description: 'Child is also attending a home school part-time.' },
    { id: 4, name: 'Not Enrolled', description: 'Child is not enrolled part-time at any other school.' }
  ];
  ngOnInit() {
    // validate the task and Action passed in
    if (!this.task || !this.taskTypeAction || !this.taskAction) {
      throw new Error('Task, TaskTypeAction, and TaskAction are required inputs');
    }

    if (!this.taskAction.id) {
      throw new Error('TaskAction must have an id');
    }

    if (!this.task.assignedStudentId) {
      throw new Error('the student id is required');
    }

    this.form.get('studentName')?.disable();
    this.form.get('studentPid')?.disable();
    this.form.get('isFullTime')?.valueChanges.subscribe((isFullTime) => {
      // Only clear coEnrollmentType when switching to full-time (not when restoring data)
      if (isFullTime) {
        this.form.patchValue({ coEnrollmentType: null }, { emitEvent: false });
      }
      const coEnrollmentControl = this.form.get('coEnrollmentType');
      if (isFullTime) {
        coEnrollmentControl?.clearValidators();
      } else {
        coEnrollmentControl?.setValidators(Validators.required);
      }
      this.cdr.markForCheck();

      coEnrollmentControl?.updateValueAndValidity();
    });

    if (this.task && this.task.assignedStudentId !== null) {
      this.studentsService
        .getStudentDetails(this.task.assignedStudentId)
        .pipe(
          take(1),
          tap((studentDetails) => {
            this.form.patchValue(
              {
                studentName: studentDetails.firstName + ' ' + studentDetails.lastName,
                studentPid: studentDetails.pidNumber
              },
              { emitEvent: false }
            );
          })
        )
        .subscribe();
    }

    if (this.taskAction?.formData) {
      // restore form data
      this.form.patchValue(JSON.parse(this.taskAction.formData), { emitEvent: false });
    }

    if (this.taskAction && this.taskAction.taskStatusId !== 1 && this.taskAction.taskStatusId !== 4) {
      this.form.disable();
    }
  }

  protected submitForm() {
    this.submitFormData.emit(JSON.stringify(this.form.getRawValue()));
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty;
  }

  public onBack(): void {
    this.router.navigate(['/']).then();
  }
}
